/**
 * LegalPage — renders Terms & Conditions or Privacy Policy based on the URL.
 * Soft-launch wording: commerce is referenced as "in development". No claims
 * about partner brands or fisheries that have not been signed.
 *
 * Anchor TOC + smooth scrolling for long-form readability on mobile.
 */
import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, Shield, ChevronRight, ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';

const slug = (s) =>
  s.toLowerCase().replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-');

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------
const LAST_UPDATED = '16 February 2026';

const TERMS_SECTIONS = [
  {
    title: '1. Welcome',
    body: [
      'These Terms and Conditions ("Terms") govern your access to and use of The Carp Cast (the "Platform"), available at thecarpcast.co.uk and via any associated apps or services. By creating an account or using the Platform you agree to these Terms.',
      'If you do not agree, please do not use the Platform.',
    ],
  },
  {
    title: '2. Who we are',
    body: [
      'The Carp Cast is a UK-based community platform built by anglers, for anglers. We provide a place to log catches, follow leagues and competitions, discover UK fisheries, share advice and (in due course) buy bait, gear and book sessions through trusted partners.',
      'Any reference to "partners" in our communications applies only to organisations with whom we have a signed agreement. Where partnerships are still being established, we say so clearly.',
    ],
  },
  {
    title: '3. Accounts',
    body: [
      'You must be 16 or over to create an account. You are responsible for keeping your login credentials secure and for any activity on your account.',
      'Please use accurate information when you sign up. We may suspend or terminate accounts that contain false information, harass other anglers, or breach these Terms.',
    ],
  },
  {
    title: '4. Acceptable use',
    body: [
      'When using the Platform you agree to:',
      '• Treat other anglers, fishery staff and partners with respect.',
      '• Only upload catch photos and details that accurately represent the fish landed.',
      '• Not impersonate other people, brands or fisheries.',
      '• Not post unlawful, harmful, abusive, defamatory, obscene or harassing content.',
      '• Not attempt to access, scrape, reverse-engineer or disrupt the Platform.',
      'Persistent or serious breaches will result in suspension or termination of your account, and where appropriate referral to the relevant authority.',
    ],
  },
  {
    title: '5. Catch uploads & user content',
    body: [
      'You retain ownership of the photos, catch reports, reviews and other content you upload ("Your Content"). By posting Your Content you grant The Carp Cast a non-exclusive, worldwide, royalty-free licence to display, store and promote it within the Platform and our community channels, with credit to your account.',
      'You are responsible for ensuring you have the right to share Your Content. We may remove content that breaches these Terms, applicable law or fishery rules.',
    ],
  },
  {
    title: '6. Memberships & subscriptions',
    body: [
      'The Carp Cast offers three membership tiers: a free Basic tier and paid Pro and Elite tiers (currently £4.99 and £9.99 per month respectively). Paid memberships are billed monthly via Stripe.',
      'You can cancel a paid membership at any time from your profile. Cancellation stops future billing; the membership remains active until the end of the current period.',
      'We may adjust pricing or perks at any time. Any change to your existing renewal price will be notified to you in advance.',
    ],
  },
  {
    title: '7. Competitions, leagues and prizes',
    body: [
      'Competitions, leagues and Catch of the Month are run for engagement and community building. Entry rules, eligibility and prize details are published on each competition page. The Carp Cast reserves the right to disqualify any entry that does not follow the rules or the spirit of fair competition.',
      'Prize fulfilment is handled by The Carp Cast or the named sponsor for each competition. Where a competition is sponsored by a partner brand or fishery, that relationship is disclosed clearly on the competition page.',
    ],
  },
  {
    title: '8. Future marketplace, bait and bookings',
    body: [
      'The Platform is in soft launch. A public marketplace, premium bait subscription boxes and direct fishery bookings are under development and will be made available as we onboard verified partners. Until then, references to these features are forward-looking and do not constitute an offer or contract.',
      'When these services go live, separate terms specific to purchases, subscriptions and bookings will be published alongside the relevant features.',
    ],
  },
  {
    title: '9. Refunds & cancellations',
    body: [
      'For memberships: you can cancel at any time. Membership fees already paid for the current period are non-refundable unless we have failed to deliver the service or applicable consumer-protection law requires otherwise.',
      'For competitions and prize draws, where an entry is paid for it is treated as a contest entry and is non-refundable once the entry deadline has passed.',
      'Refund and cancellation terms specific to the marketplace and fishery bookings will be published when those services launch.',
    ],
  },
  {
    title: '10. Community moderation',
    body: [
      'We may, at our discretion, remove posts, comments, reviews or accounts that breach these Terms or that are reported by the community. We are not obliged to monitor content proactively but we do take reasonable steps once a credible report is made.',
    ],
  },
  {
    title: '11. Liability',
    body: [
      'Nothing in these Terms limits any liability that cannot be limited under English law (including liability for fraud, death or personal injury caused by negligence).',
      'Subject to that, The Carp Cast is not liable for any indirect or consequential loss, loss of data, lost profit, or loss arising from third-party content. Where you book sessions, purchase products or follow advice from third parties via the Platform, your contract is with that third party and not with The Carp Cast.',
    ],
  },
  {
    title: '12. Account termination',
    body: [
      'You can close your account at any time from your profile. We will retain anonymous catch data and competition records as outlined in our Privacy Policy.',
      'We may suspend or terminate your account if you breach these Terms, if your use of the Platform creates legal or reputational risk, or if we discontinue the Platform.',
    ],
  },
  {
    title: '13. Changes to these Terms',
    body: [
      "We may update these Terms from time to time. Material changes will be notified inside the Platform and (where significant) by email. Continued use of the Platform after a change means you accept the updated Terms.",
    ],
  },
  {
    title: '14. Governing law',
    body: [
      'These Terms are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.',
    ],
  },
  {
    title: '15. Contact',
    body: [
      'Questions about these Terms? Email support@thecarpcast.co.uk or use the contact form.',
    ],
  },
];

const PRIVACY_SECTIONS = [
  {
    title: '1. Introduction',
    body: [
      'This Privacy Policy explains what personal information The Carp Cast collects, how we use it, who we share it with, and the rights you have over it. It applies to anyone who creates an account, contacts us, joins a waitlist, or otherwise interacts with the Platform.',
      'We are committed to handling your data fairly and transparently in line with UK GDPR.',
    ],
  },
  {
    title: '2. Who is responsible for your data',
    body: [
      'The Carp Cast is the data controller for the information described in this policy. You can contact us at support@thecarpcast.co.uk for any data-related queries.',
    ],
  },
  {
    title: '3. What we collect',
    body: [
      'When you sign up: your username, email address, password (hashed), and basic profile preferences.',
      'When you log a catch: lake, fish species, weight, optional photo and notes, time and date.',
      'When you join a competition or league: your entry submissions and scores.',
      'When you take out a paid membership: payment is processed by Stripe. We do not store full card details. Stripe shares a customer ID, the last 4 digits of the card and the billing country with us so we can support your account.',
      'When you contact us or join a waitlist: your name, email and the message or interest you indicated.',
      'When you use the Platform: standard server logs (IP, browser, pages viewed) and lightweight analytics events (e.g. which premium features anglers click on most). These are used in aggregate to improve the product.',
    ],
  },
  {
    title: '4. Why we use it',
    body: [
      '• To provide your account, catch log, leagues, competitions and memberships.',
      '• To send transactional emails (account confirmations, password resets, membership receipts, prize notifications).',
      "• To send membership-related and product-update emails. You can opt out of non-essential emails at any time from your profile.",
      '• To moderate community content and prevent abuse, fraud or unauthorised use of the Platform.',
      '• To improve the Platform — understanding which features are useful, where the friction is, and what to build next.',
      '• To meet our legal obligations (e.g. tax, accounting, requests from law enforcement where lawful).',
    ],
  },
  {
    title: '5. Cookies & analytics',
    body: [
      "We use a small number of strictly necessary cookies (e.g. to keep you signed in) and aggregated analytics events to understand product usage. We do not run third-party advertising trackers.",
      'You can clear cookies from your browser at any time. If you sign out, your session cookie is removed.',
    ],
  },
  {
    title: '6. Payments via Stripe',
    body: [
      "Membership payments are processed by Stripe Payments Europe Ltd. Stripe is the data processor for card details — those never touch our servers. Stripe's privacy policy is available at stripe.com/privacy.",
      'When you start, change or cancel a membership we receive a notification (called a webhook) from Stripe so we can update your account. The webhook itself contains your Stripe customer ID and event metadata — not your card details.',
    ],
  },
  {
    title: '7. Email via Resend',
    body: [
      'Transactional and product emails (account confirmations, membership notifications, occasional product updates) are sent through Resend, our email delivery provider. Resend handles delivery, bounces and basic engagement metrics on our behalf and acts as a data processor.',
      'You can opt out of non-essential email at any time using the unsubscribe link in the footer of any marketing email, or by emailing support@thecarpcast.co.uk.',
    ],
  },
  {
    title: '8. Photos and catch content',
    body: [
      'When you upload a catch photo, it is hosted by us and shown on the Platform alongside your username. If you delete a catch the public-facing entry is removed; we may retain anonymous metadata (species, weight, lake) in aggregate competition records.',
    ],
  },
  {
    title: '9. Who we share data with',
    body: [
      "• Stripe — for processing payments (see above).",
      '• Resend — for sending email (see above).',
      "• Our cloud and database hosting providers — strictly for running the Platform under appropriate data-processing agreements.",
      '• Fishery and brand partners — only where you have explicitly opted into being shared with that partner (e.g. by entering a sponsored competition or buying from a partner). We will tell you what data will be shared and why before you do so.',
      '• Law enforcement or regulators — where we are legally required.',
      'We never sell your personal data.',
    ],
  },
  {
    title: '10. How long we keep it',
    body: [
      'We keep your account data for as long as your account is active. If you close your account, we retain payment and tax records for as long as required by law, and we anonymise competition and league records so the historical leaderboards remain valid.',
    ],
  },
  {
    title: '11. Your rights',
    body: [
      'Under UK GDPR you have the right to:',
      '• Access the personal data we hold about you.',
      '• Correct inaccurate data.',
      '• Request deletion of your data, subject to legal retention requirements.',
      '• Object to or restrict certain types of processing.',
      '• Receive a copy of your data in a portable format.',
      'To exercise any of these rights, email support@thecarpcast.co.uk. We aim to respond within 30 days.',
    ],
  },
  {
    title: '12. Account deletion',
    body: [
      'You can request deletion of your account from your profile or by emailing support@thecarpcast.co.uk. We will confirm by email, then delete your personal data within 30 days, retaining only what we are legally required to keep (e.g. tax records on past payments) or what is fully anonymised.',
    ],
  },
  {
    title: '13. Security',
    body: [
      'We take security seriously. Passwords are stored hashed (bcrypt). All traffic to the Platform is encrypted via HTTPS. We restrict access to personal data to staff who need it to do their jobs.',
      'No system is 100% secure — if you suspect your account has been compromised, please reset your password and email support@thecarpcast.co.uk immediately.',
    ],
  },
  {
    title: '14. Children',
    body: [
      'The Platform is intended for users aged 16 and over. If you believe a child under 16 has provided personal data to us, please contact support@thecarpcast.co.uk and we will delete it.',
    ],
  },
  {
    title: '15. Changes to this policy',
    body: [
      'We may update this Privacy Policy from time to time. Material changes will be notified inside the Platform and (where significant) by email.',
    ],
  },
  {
    title: '16. Contact',
    body: [
      'Questions about your privacy? Email support@thecarpcast.co.uk or use the contact form. If you are unhappy with our response, you can also raise a complaint with the UK Information Commissioner\'s Office (ico.org.uk).',
    ],
  },
];

const CONTENT_BY_ROUTE = {
  '/terms':   { kind: 'terms',   title: 'Terms & Conditions', icon: FileText, sections: TERMS_SECTIONS,
                intro: 'The rules of the road. Read once, fish forever.' },
  '/privacy': { kind: 'privacy', title: 'Privacy Policy',     icon: Shield,   sections: PRIVACY_SECTIONS,
                intro: 'What we collect, why, and how you stay in control.' },
};

const SectionBlock = ({ section }) => (
  <section
    id={slug(section.title)}
    className="scroll-mt-28 mb-10"
    data-testid={`legal-section-${slug(section.title)}`}
  >
    <h2 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold uppercase tracking-wider text-white mb-4">
      {section.title}
    </h2>
    <div className="space-y-3 text-[#cfd4cf] leading-relaxed text-base">
      {section.body.map((para, i) => (
        <p key={i} className="whitespace-pre-line">{para}</p>
      ))}
    </div>
  </section>
);

const LegalPage = () => {
  const location = useLocation();
  const config = CONTENT_BY_ROUTE[location.pathname] || CONTENT_BY_ROUTE['/terms'];

  const toc = useMemo(
    () => config.sections.map((s) => ({ id: slug(s.title), title: s.title })),
    [config.sections],
  );

  const Icon = config.icon;

  return (
    <div className="min-h-screen bg-[#050605]" data-testid={`legal-page-${config.kind}`}>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(200,178,115,0.10),transparent_60%)] pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-12 py-14 md:py-20">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs text-[#8a948a] hover:text-white transition-colors mb-6"
            data-testid="legal-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to The Carp Cast
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#c8b273]/30 bg-[#c8b273]/5 px-4 py-1.5 mb-4"
          >
            <Icon className="w-3.5 h-3.5 text-[#c8b273]" />
            <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#c8b273]">
              Legal
            </span>
          </motion.div>
          <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-extrabold uppercase tracking-wider text-white mb-3">
            {config.title}
          </h1>
          <p className="text-[#a8b0a8] max-w-2xl">{config.intro}</p>
          <p className="mt-4 text-xs uppercase tracking-widest text-[#6b7d6b]">
            Last updated · {LAST_UPDATED}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* TOC */}
          <aside className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-2xl border border-white/10 bg-[#0b0d0b] p-5">
              <p className="data-label text-[#c8b273] mb-3">Contents</p>
              <ul className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
                {toc.map((it) => (
                  <li key={it.id}>
                    <a
                      href={`#${it.id}`}
                      className="group flex items-start gap-2 px-2 py-1.5 rounded text-sm text-[#a8b0a8] hover:text-white hover:bg-white/5 transition-colors"
                      data-testid={`legal-toc-${it.id}`}
                    >
                      <ChevronRight className="w-3.5 h-3.5 mt-0.5 text-[#6b7d6b] group-hover:text-[#c8b273]" />
                      <span>{it.title}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Body */}
          <article className="lg:col-span-8" data-testid="legal-body">
            {config.sections.map((s) => (
              <SectionBlock key={s.title} section={s} />
            ))}

            <div className="mt-12 pt-8 border-t border-white/10 text-sm text-[#8a948a]">
              <p>
                Need a hand? Visit our{' '}
                <Link to="/contact" className="text-[#c8b273] hover:underline">
                  Contact page
                </Link>{' '}
                or email{' '}
                <a
                  href="mailto:support@thecarpcast.co.uk"
                  className="text-[#c8b273] hover:underline"
                >
                  support@thecarpcast.co.uk
                </a>
                .
              </p>
            </div>
          </article>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LegalPage;
